import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import environment from '@environments/environment';
import { Role } from '@shared/interfaces/permissions.interface';
import { CUSTOM_ERROR_HANDLING } from '@shared/http';
import {
  MaintenanceModeStatus,
  MaintenanceMode,
  EnableMaintenanceRequest,
  ScheduleMaintenanceRequest,
  CancelMaintenanceRequest,
  MaintenanceHistoryResponse,
} from './maintenance.types';

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly __http = inject(HttpClient);

  /**
   * Check if user can bypass maintenance mode
   * IT Admins can access the system during maintenance
   * If user is being impersonated, check if the impersonator can bypass (not the impersonated user)
   * @param user - The current user object
   * @returns true if user can bypass maintenance
   */
  canBypassMaintenance(user?: unknown): boolean {
    if (!user || typeof user !== 'object') {
      return false;
    }

    // If user is being impersonated, always allow bypass
    // (Only IT Admins can impersonate, so the real user is an IT Admin)
    if ('is_impersonating' in user && user.is_impersonating) {
      return true;
    }

    // Right now, only ITAdmin role(s) are granted access during times of
    // maintenance.  This check will allow a smoother user experience, as the
    // API already has its own check(s).

    // Check role - can be either a string or object with name property
    if ('role' in user) {
      const role = user.role;
      // If role is a string, compare directly
      if (typeof role === 'string') {
        return role === Role.ITAdmin;
      }
      // If role is an object with name property
      if (role && typeof role === 'object' && 'name' in role) {
        return role.name === Role.ITAdmin;
      }
    }

    return false;
  }

  /**
   * Get current maintenance mode status (public endpoint)
   * @param days - Number of days to look ahead for scheduled maintenance (default 14)
   */
  getStatus(days?: number): Observable<MaintenanceModeStatus> {
    const params = new HttpParams();
    if (days !== undefined) {
      params.set('days', days.toString());
    }
    return this.__http.get<MaintenanceModeStatus>(
      `${environment.irisApi}/settings/maintenance/`,
      {
        params,
        context: new HttpContext().set(CUSTOM_ERROR_HANDLING, 'silent-mode'),
      },
    );
  }

  /**
   * Enable maintenance mode now
   */
  enableEmergencyMaintenance(
    request: EnableMaintenanceRequest,
  ): Observable<MaintenanceMode> {
    return this.__http.post<MaintenanceMode>(
      `${environment.irisApi}/admin/settings/maintenance/enable/`,
      request,
    );
  }

  /** Enable mtainenance mode for a given maintenance record */
  enableScheduledMaintenance(
    maintenanceId: string,
    request: EnableMaintenanceRequest,
  ): Observable<MaintenanceMode> {
    return this.__http.post<MaintenanceMode>(
      `${environment.irisApi}/admin/settings/maintenance/enable/${maintenanceId}/`,
      request,
    );
  }

  /**
   * Disable/complete active maintenance
   */
  disableMaintenance(maintenanceId: string): Observable<MaintenanceMode> {
    return this.__http.post<MaintenanceMode>(
      `${environment.irisApi}/admin/settings/maintenance/disable/${maintenanceId}/`,
      {},
    );
  }

  /**
   * Schedule future maintenance
   */
  scheduleMaintenance(
    request: ScheduleMaintenanceRequest,
  ): Observable<MaintenanceMode> {
    return this.__http.post<MaintenanceMode>(
      `${environment.irisApi}/admin/settings/maintenance/schedule/`,
      request,
    );
  }

  /**
   * Cancel scheduled maintenance
   */
  cancelScheduledMaintenance(
    maintenanceId: string,
    request?: CancelMaintenanceRequest,
  ): Observable<MaintenanceMode> {
    return this.__http.post<MaintenanceMode>(
      `${environment.irisApi}/admin/settings/maintenance/cancel/${maintenanceId}/`,
      request || {},
    );
  }

  /**
   * Get all maintenance records with optional filtering and sorting
   */
  getAllMaintenance(
    page: number = 1,
    pageSize: number = 50,
    status?: string,
    reason?: string,
    sortBy: string = 'scheduled_start',
    sortOrder: string = 'desc',
  ): Observable<MaintenanceHistoryResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString())
      .set('sort_by', sortBy)
      .set('sort_order', sortOrder);

    if (status) {
      params = params.set('status', status);
    }
    if (reason) {
      params = params.set('reason', reason);
    }

    return this.__http.get<MaintenanceHistoryResponse>(
      `${environment.irisApi}/admin/settings/maintenance/history/`,
      { params },
    );
  }

  /**
   * Get all maintenance records
   */
  getHistoryForMaintenance(
    maintenanceId: string,
    page: number = 1,
    pageSize: number = 50,
  ): Observable<MaintenanceHistoryResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());
    return this.__http.get<MaintenanceHistoryResponse>(
      `${environment.irisApi}/admin/settings/maintenance/history/${maintenanceId}/`,
      { params },
    );
  }
}
